// Preparing a greeting in the browser: a picked file (kept byte for byte when it is already canonical, otherwise
// decoded by the browser and converted) or an in-page microphone recording (MediaRecorder, then the same
// conversion). Everything that touches the browser comes in through RecorderEnv, so tests pass fakes.
// Once the microphone is allowed, a three-second countdown runs before MediaRecorder starts, so the first words
// aren't clipped. The microphone is released as soon as a recording stops or is cancelled, countdown included.
import { LIMITS } from './model.ts';
import { MAX_SOURCE_BYTES, canonicalGreeting, checkGreetingWav } from './wav.ts';

export interface DecodedAudio { channels: Float32Array[]; sampleRate: number }

export interface Prepared {
  wav: Uint8Array<ArrayBuffer>;
  durationMs: number;
  source: 'file' | 'recording';
  /** True when the browser converted the audio (not uploaded byte for byte). */
  converted: boolean;
  name: string | null;
}
export type PrepareResult = { ok: true; prepared: Prepared } | { ok: false; code: string };

/** Minimal shapes of the browser APIs used; the real ones satisfy them. */
export interface TrackLike { stop(): void }
export interface StreamLike { getTracks(): TrackLike[] }
export interface MediaRecorderLike {
  state: string;
  mimeType: string;
  ondataavailable: ((event: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: (() => void) | null;
  start(timeslice?: number): void;
  stop(): void;
}

export interface RecorderEnv {
  secure: boolean;
  getUserMedia: ((constraints: { audio: Record<string, boolean | number> }) => Promise<StreamLike>) | null;
  createRecorder: ((stream: StreamLike) => MediaRecorderLike) | null;
  decode: ((data: ArrayBuffer) => Promise<DecodedAudio>) | null;
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
  now: () => number;
}

/** The real browser, feature-detected. Missing pieces are null, never polyfilled. */
export function browserEnv(): RecorderEnv {
  const g = globalThis as unknown as {
    isSecureContext?: boolean;
    navigator?: { mediaDevices?: { getUserMedia?: (c: unknown) => Promise<StreamLike> } };
    MediaRecorder?: new (stream: StreamLike) => MediaRecorderLike;
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  const media = g.navigator?.mediaDevices;
  const Recorder = g.MediaRecorder;
  const Ctx = g.AudioContext ?? g.webkitAudioContext;
  return {
    secure: g.isSecureContext !== false,
    getUserMedia: media?.getUserMedia ? constraints => media.getUserMedia!.call(media, constraints) : null,
    createRecorder: Recorder ? stream => new Recorder(stream) : null,
    decode: Ctx
      ? async data => {
          const ctx = new Ctx();
          try {
            const buffer = await ctx.decodeAudioData(data);
            return { channels: Array.from({ length: buffer.numberOfChannels }, (_, i) => buffer.getChannelData(i)), sampleRate: buffer.sampleRate };
          } finally {
            void ctx.close?.().catch(() => undefined);
          }
        }
      : null,
    setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
    clearTimeout: handle => globalThis.clearTimeout(handle as ReturnType<typeof globalThis.setTimeout>),
    now: () => Date.now(),
  };
}
type AudioContextCtor = new () => {
  decodeAudioData(data: ArrayBuffer): Promise<{ numberOfChannels: number; sampleRate: number; getChannelData(i: number): Float32Array }>;
  close?: () => Promise<void>;
};

/** Why the in-page recorder isn't offered, or null when it is. */
export function recorderUnavailable(env: RecorderEnv): 'insecure' | 'no_microphone_api' | 'no_recorder' | 'no_decoder' | null {
  if (!env.secure) return 'insecure';
  if (!env.getUserMedia) return 'no_microphone_api';
  if (!env.createRecorder) return 'no_recorder';
  if (!env.decode) return 'no_decoder';
  return null;
}

export const UNAVAILABLE_COPY = {
  insecure: 'Recording here needs a secure (https) connection. Upload a file instead.',
  no_microphone_api: 'This browser can’t use a microphone here. Upload a file instead.',
  no_recorder: 'This browser can’t record audio here. Upload a file instead.',
  no_decoder: 'This browser can’t convert audio here. Upload a WAV file in the phone format instead.',
} as const;

/** Trims decoded audio to the greeting's maximum length (a recording stopped by the time limit runs a little over). */
function trim(decoded: DecodedAudio): DecodedAudio {
  const max = Math.floor((decoded.sampleRate * LIMITS.greetingMaxMs) / 1000);
  return { sampleRate: decoded.sampleRate, channels: decoded.channels.map(c => (c.length > max ? c.subarray(0, max) : c)) };
}

/**
 * A picked file: canonical WAV is kept byte for byte. A canonical file with a bad length is refused (converting
 * wouldn't change its length). Anything else is decoded by the browser and converted, when the browser can.
 */
export async function prepareFile(bytes: Uint8Array<ArrayBuffer>, name: string | null, decode: RecorderEnv['decode']): Promise<PrepareResult> {
  if (bytes.length > MAX_SOURCE_BYTES) return { ok: false, code: 'file_too_big' };
  const check = checkGreetingWav(bytes);
  if (check.ok) return { ok: true, prepared: { wav: bytes, durationMs: check.durationMs, source: 'file', converted: false, name } };
  if (check.code === 'greeting_too_short' || check.code === 'greeting_too_long') return { ok: false, code: check.code };
  if (!decode) return { ok: false, code: check.code };
  let decoded: DecodedAudio;
  try {
    decoded = await decode(bytes.slice().buffer);
  } catch {
    return { ok: false, code: check.code === 'greeting_format_unsupported' ? check.code : 'not_audio' };
  }
  const result = canonicalGreeting(decoded.channels, decoded.sampleRate);
  return result.ok ? { ok: true, prepared: { wav: result.wav, durationMs: result.durationMs, source: 'file', converted: true, name } } : { ok: false, code: result.code };
}

/** Seconds between the microphone being allowed and the recording starting (shown as 3, 2, 1). */
export const COUNTDOWN_SECONDS = 3;

export type RecorderSnapshot =
  | { state: 'idle' }
  | { state: 'requesting' }
  | { state: 'countdown'; remainingSeconds: number }
  | { state: 'recording'; elapsedMs: number }
  | { state: 'processing' }
  | { state: 'error'; code: 'denied' | 'no_microphone' | 'busy' | 'failed' | 'greeting_too_short' | 'greeting_too_long' | 'no_audio' | 'not_audio' };

export const RECORDER_ERROR_COPY: Record<Extract<RecorderSnapshot, { state: 'error' }>['code'], string> = {
  denied: 'Microphone access was blocked. Allow it in your browser’s site settings, or upload a file instead.',
  no_microphone: 'No microphone was found. Connect one, or upload a file instead.',
  busy: 'The microphone is in use by another app. Close it, then try again.',
  failed: 'The recording stopped unexpectedly. Try again, or upload a file instead.',
  greeting_too_short: 'That recording was shorter than 1 second. Record again.',
  greeting_too_long: 'That recording was longer than 60 seconds. Record again.',
  no_audio: 'No sound was recorded. Check the microphone, then record again.',
  not_audio: 'This browser couldn’t convert the recording. Upload a file instead.',
};

/** One in-page microphone recording at a time, after a short countdown, stopped by the user or at 60 seconds. */
export class GreetingRecorder {
  #snapshot: RecorderSnapshot = { state: 'idle' };
  readonly #listeners = new Set<() => void>();
  #stream: StreamLike | null = null;
  #recorder: MediaRecorderLike | null = null;
  #chunks: Blob[] = [];
  #startedAt = 0;
  #timers: unknown[] = [];
  #cancelled = false;
  #generation = 0;

  constructor(private readonly env: RecorderEnv, private readonly onPrepared: (prepared: Prepared) => void) {}

  getSnapshot = (): RecorderSnapshot => this.#snapshot;
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };
  #set(next: RecorderSnapshot): void {
    this.#snapshot = next;
    for (const l of [...this.#listeners]) l();
  }

  async start(): Promise<void> {
    const busy = this.#snapshot.state;
    if (busy === 'requesting' || busy === 'countdown' || busy === 'recording' || busy === 'processing') return;
    if (recorderUnavailable(this.env) || !this.env.getUserMedia || !this.env.createRecorder) return;
    const generation = ++this.#generation;
    this.#cancelled = false;
    this.#set({ state: 'requesting' });
    let stream: StreamLike;
    try {
      stream = await this.env.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
    } catch (error) {
      if (generation !== this.#generation) return;
      const name = typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : undefined;
      this.#set({ state: 'error', code: name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : name === 'NotFoundError' || name === 'OverconstrainedError' ? 'no_microphone' : name === 'NotReadableError' || name === 'AbortError' ? 'busy' : 'failed' });
      return;
    }
    if (generation !== this.#generation || this.#cancelled) {
      for (const t of stream.getTracks()) t.stop();
      return;
    }
    this.#stream = stream;
    this.#set({ state: 'countdown', remainingSeconds: COUNTDOWN_SECONDS });
    this.#countdown(generation, COUNTDOWN_SECONDS);
  }

  /** One second of the countdown; at zero, the recording starts. Cancel clears the timer and releases the microphone. */
  #countdown(generation: number, remaining: number): void {
    this.#timers.push(this.env.setTimeout(() => {
      if (generation !== this.#generation || this.#cancelled || this.#snapshot.state !== 'countdown') return;
      const next = remaining - 1;
      if (next > 0) {
        this.#set({ state: 'countdown', remainingSeconds: next });
        this.#countdown(generation, next);
      } else {
        this.#record(generation);
      }
    }, 1000));
  }

  #record(generation: number): void {
    const stream = this.#stream;
    if (!stream || !this.env.createRecorder) {
      this.#release();
      this.#set({ state: 'error', code: 'failed' });
      return;
    }
    let recorder: MediaRecorderLike;
    try {
      recorder = this.env.createRecorder(stream);
    } catch {
      this.#release();
      this.#set({ state: 'error', code: 'failed' });
      return;
    }
    this.#recorder = recorder;
    this.#chunks = [];
    recorder.ondataavailable = event => { if (event.data && event.data.size > 0) this.#chunks.push(event.data); };
    recorder.onerror = () => { if (generation === this.#generation) { this.#release(); this.#set({ state: 'error', code: 'failed' }); } };
    recorder.onstop = () => void this.#finish(generation, recorder.mimeType);
    try {
      recorder.start();
    } catch {
      this.#release();
      this.#set({ state: 'error', code: 'failed' });
      return;
    }
    this.#startedAt = this.env.now();
    this.#set({ state: 'recording', elapsedMs: 0 });
    this.#timers.push(this.env.setTimeout(() => this.stop(), LIMITS.greetingMaxMs));
    this.#tick(generation);
  }

  /** Updates the elapsed time shown while recording (local clock only; nothing is requested). */
  #tick(generation: number): void {
    this.#timers.push(this.env.setTimeout(() => {
      if (generation !== this.#generation || this.#snapshot.state !== 'recording') return;
      this.#set({ state: 'recording', elapsedMs: Math.min(LIMITS.greetingMaxMs, this.env.now() - this.#startedAt) });
      this.#tick(generation);
    }, 250));
  }

  stop(): void {
    if (this.#snapshot.state !== 'recording' || !this.#recorder) return;
    this.#set({ state: 'processing' });
    this.#clearTimers();
    try {
      if (this.#recorder.state !== 'inactive') this.#recorder.stop();
    } catch {
      this.#release();
      this.#set({ state: 'error', code: 'failed' });
    }
  }

  /** Stops without keeping anything and releases the microphone. */
  cancel(): void {
    this.#cancelled = true;
    this.#generation += 1;
    this.#clearTimers();
    try {
      if (this.#recorder && this.#recorder.state !== 'inactive') this.#recorder.stop();
    } catch { /* already stopped */ }
    this.#release();
    this.#chunks = [];
    this.#set({ state: 'idle' });
  }

  dispose(): void {
    this.cancel();
    this.#listeners.clear();
  }

  /** Back to idle after an error or once the prepared greeting was taken. */
  reset(): void {
    if (this.#snapshot.state === 'error') this.#set({ state: 'idle' });
  }

  async #finish(generation: number, mimeType: string): Promise<void> {
    this.#release();
    if (generation !== this.#generation || this.#cancelled) return;
    this.#set({ state: 'processing' });
    const chunks = this.#chunks;
    this.#chunks = [];
    if (!chunks.length || !this.env.decode) {
      this.#set({ state: 'error', code: chunks.length ? 'not_audio' : 'no_audio' });
      return;
    }
    const current = () => generation === this.#generation && !this.#cancelled;
    let result: ReturnType<typeof canonicalGreeting>;
    try {
      const data = await new Blob(chunks, mimeType ? { type: mimeType } : {}).arrayBuffer();
      if (!current()) return;
      const decoded = await this.env.decode(data);
      if (!current()) return;
      result = canonicalGreeting(trim(decoded).channels, decoded.sampleRate);
    } catch {
      if (current()) this.#set({ state: 'error', code: 'not_audio' });
      return;
    }
    if (!result.ok) {
      this.#set({ state: 'error', code: result.code });
      return;
    }
    this.#set({ state: 'idle' });
    this.onPrepared({ wav: result.wav, durationMs: result.durationMs, source: 'recording', converted: true, name: null });
  }

  #clearTimers(): void {
    for (const t of this.#timers) this.env.clearTimeout(t);
    this.#timers = [];
  }

  #release(): void {
    for (const t of this.#stream?.getTracks() ?? []) {
      try { t.stop(); } catch { /* already stopped */ }
    }
    this.#stream = null;
    this.#recorder = null;
  }
}
